import { useCallback, useEffect, useState } from 'react';

import { MInput, MSkeleton } from '@Components/Base';
import { getModalRecordKey } from '@Store/selectors/modalSelector';
import { useAppSelector } from '@Hooks/index';
import {
  DocumentListResponse,
  MaterialRequisitionDocumentResponse,
} from '@Types/api/form';
import { getDocument, getFileTypeExt } from '@Features/utils/document';
import { useFormListMutation } from '@Store/api/dmsApiSlice';
import { useGetRequisitionDocumentsMutation } from '@Features/MaterialRequest/requests/MaterialRequisition/store/api/MaterialRequestApiSlice';

import { NoData } from '@Components/RequestList/noData';
import MaterialDocumentItem from './MaterialDocumentItem';

import styles from '../view-tab.module.css';
import { findDocumentItemBySearchValue } from '../../../../../utils/finder';
import { getDocumentFolder } from '../../../utils/document';

const DocumentsTab: React.FC = () => {
  const requisitionId = useAppSelector(getModalRecordKey);
  const [getRequisitionDocuments, { isSuccess }] =
    useGetRequisitionDocumentsMutation();
  const [getFormList] = useFormListMutation();
  const [listItems, setListItems] =
    useState<Array<MaterialRequisitionDocumentResponse>>();
  const [searchedDocItems, setSearchedDocItems] =
    useState<Array<MaterialRequisitionDocumentResponse>>();
  const [documentsInFolder, setDocumentsInFolder] =
    useState<DocumentListResponse[]>();

  const handleSearchDocumentItems = useCallback(
    (value: string) => {
      const result = findDocumentItemBySearchValue(listItems!, value);
      setSearchedDocItems(result);
    },
    [listItems]
  );

  useEffect(() => {
    if (listItems?.length) {
      setSearchedDocItems(listItems);
    }
  }, [listItems]);

  const handleDownload = (url: string | undefined, filePath: string): void => {
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  useEffect(() => {
    if (requisitionId) {
      const documentFolderPath = getDocumentFolder(requisitionId);

      getRequisitionDocuments(requisitionId)
        .unwrap()
        .then((response) => {
          setListItems(response);
        });

      getFormList(documentFolderPath)
        .unwrap()
        .then((response) => {
          setDocumentsInFolder(response);
        });
    }
  }, [requisitionId]);

  return (
    <div className={styles.viewTabMaterials}>
      <MInput
        className={styles.search}
        searchInput
        placeholder="Search..."
        onChange={(event) => handleSearchDocumentItems(event.target.value)}
      />
      {isSuccess ? (
        <div className={styles.container}>
          {searchedDocItems?.length ? (
            <>
              {searchedDocItems.map((document) => {
                // TODO: need to check docExtId instead of getDocument
                const matchedDocument =
                  documentsInFolder &&
                  getDocument(documentsInFolder, document.filePath);

                return (
                  <MaterialDocumentItem
                    description={document.docDesc}
                    name={document.docName}
                    status={Boolean(document.deleteFlag)}
                    type={getFileTypeExt(document.filePath)}
                    key={`${document.docName}-${document.filePath}`}
                    isConfidential={Boolean(document.isConfidential)}
                    filePath={document.filePath}
                    documentId={matchedDocument?.documentId}
                    onDownload={handleDownload}
                  />
                );
              })}
            </>
          ) : (
            <NoData context="documents" />
          )}
        </div>
      ) : (
        <div className={styles.container}>
          <MSkeleton repeat={2} className="skeleton-item" />
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;
